import { useState } from 'react'
import type { LiveModel } from '../adapter'
import { t } from '../../i18n'
import { Button, Icon } from '../../ui'

/**
 * The bottom bar: Input ▾, All on, All off, End everywhere (= All off). There is no
 * bulk endpoint yet: a PATCH per output (stage 2: POST /api/destinations/bulk). While
 * live, turning outputs off asks in the bar itself, with the list of what goes off air:
 * no modal on top of a broadcast (owner 23.10). Buttons are the system's: End is a plain button,
 * `danger` only on the confirming step (Buttons «danger only for the destructive step», CB-04).
 */
export function ControlBar({ m, phone, inputOpen, onInput, onAll }: {
  m: LiveModel; phone?: boolean; inputOpen?: boolean; onInput: () => void; onAll: (on: boolean) => Promise<void>;
}) {
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState<'on' | 'off' | null>(null)
  const live = m.summary.live
  const enabled = m.dests.filter((d) => d.enabled)
  const off = m.dests.filter((d) => !d.enabled)
  const going = enabled.filter((d) => d.state === 'live')
  const proto = m.source.protocol?.toUpperCase()

  const run = async (on: boolean) => {
    setBusy(on ? 'on' : 'off')
    setConfirm(false)
    try {
      await onAll(on)
    } finally {
      setBusy(null)
    }
  }
  const allOff = () => (live && going.length ? setConfirm(true) : void run(false))

  if (confirm) {
    return (
      <div role="alertdialog" aria-label={t('bar.confirmOff.title')} data-testid="all-off-confirm"
        className="flex flex-none flex-wrap items-center gap-2 border-t border-fail/45 bg-fail/[.07] px-3 py-2.5 md:px-5">
        <span className="flex items-center gap-2 font-medium text-fail"><Icon name="state-fail" size={16} />{t('bar.confirmOff.title')}</span>
        <span data-testid="all-off-list" className="min-w-0 flex-1 text-[13px] text-text-soft">
          {t('bar.confirmOff.body', { names: going.map((d) => d.name).join(', ') })}
        </span>
        <Button variant="ghost" onClick={() => setConfirm(false)} className="max-md:min-h-[44px]">{t('bar.keep')}</Button>
        <Button variant="danger" data-testid="all-off-yes" onClick={() => void run(false)} className="max-md:min-h-[44px]">
          {t('bar.confirmOff.yes', { n: going.length })}
        </Button>
      </div>
    )
  }

  const btn = 'max-md:min-h-[44px]'
  return (
    <nav aria-label={t('bar.controls')} data-testid="control-bar"
      className={`flex flex-none flex-wrap items-center gap-2 border-t border-line bg-panel px-3 py-2.5 md:px-5 ${phone ? 'sticky bottom-0 z-10' : ''}`}>
      <Button data-testid="bar-input" aria-expanded={inputOpen} onClick={onInput} className="max-md:min-h-[44px]">
        {!phone && <span className="text-muted">{t('strip.input')}</span>}<span className="font-mono text-[13px]">{proto ?? 'RTMPS / SRT'}</span><Icon name="chevron-down" size={16} className="text-muted" />
      </Button>
      {!phone && <span aria-hidden className="mx-1 h-6 w-px bg-line" />}
      <Button data-testid="all-on" disabled={!off.length || !!busy} onClick={() => void run(true)} className={btn}>{busy === 'on' ? '…' : t('bar.allOn')}</Button>
      <Button data-testid="all-off" disabled={!enabled.length || !!busy} onClick={allOff} className={btn}>{busy === 'off' ? '…' : t('bar.allOff')}</Button>
      {/* ml-auto on the item itself, not a spacer element: a spacer is one more gap (8 px)
          in the row, and at 390 px that pushed End past the edge (fill-phone.spec.ts). */}
      {live ? (
        <Button data-testid="end-all" disabled={!enabled.length || !!busy} onClick={allOff} className="ml-auto max-md:min-h-[44px]">
          {phone ? t('bar.end') : t('bar.endAll')}
        </Button>
      ) : (
        !phone && <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-xs text-muted"><Icon name="state-idle" size={12} />{t('bar.waiting')}</span>
      )}
    </nav>
  )
}
